<template>
    <div data-testid="user-permission-add-access" class="">
        <div class="d-flex flex-column gap-4 pa-6 pb-16">
            <!-- Header Section -->
            <div class="d-flex align-center justify-space-between">
                <div class="d-flex flex-column gap-3">
                    <!-- Breadcrumbs Row -->
                    <v-breadcrumbs data-testid="user-permission-add-access__breadcrumbs" divider="/" large :items="items"
                        class="pa-0"></v-breadcrumbs>

                    <!-- Title Row with Actions on Right -->
                    <!-- Left: Back Button + Title -->
                    <div class="d-flex align-center gap-2" data-testid="user-permission-add-access__title-section">
                        <v-btn data-testid="user-permission-add-access__back-button" icon small
                            class="" @click="$router.back()">
                            <v-icon>mdi-arrow-left</v-icon>
                        </v-btn>
                        <div data-testid="user-permission-add-access__title" class="d-flex align-center gap-2">
                            Manage Access For -  
                            <h4 v-if="loading || !userName" class="breadcrumb-shimmer breadcrumb-shimmer--title"></h4>
                            <h4 v-else class="">{{ userName }}</h4>
                        </div>
                    </div>
                </div>
                <div class="d-flex align-center justify-space-between">

                    <!-- Right: Dropdown + Activity Log -->
                    <div class="d-flex align-center gap-2" data-testid="user-permission-add-access__actions">
                        <h6>Set User as - </h6>
                        <v-tooltip bottom :disabled="!isCurrentUser">
                            <template v-slot:activator="{ on, attrs }">
                                <div v-bind="attrs" v-on="on">
                                    <lb-radiodropdown v-model="cc" :items="rolesList" label="Select an option"
                                        itemtext="name" itemvalue="_id" width="300px" search-prefix-icon="mdi-filter"
                                        :disabled="loading || isCurrentUser"
                                        data-testid="user-permission-add-access__status-dropdown" @change="handleChange" />
                                </div>
                            </template>
                            <span>You cannot change your own role</span>
                        </v-tooltip>
                        <v-tooltip left content-class="tooltip-left"
                            data-testid="user-permission-add-access__activitylog-tooltip">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on"
                                    data-testid="user-permission-add-access__activitylog-activator">
                                    <lb-activitylog data-testid="user-permission-add-access__activity-log"
                                        :config="activitylogconfiguserrole"
                                        :url="`/v2/varianceanalysis/userpermission/getlogs/` + computedUserId"
                                        contentClass="tooltip-bottom" />
                                </span>
                            </template>
                            <span>Activity Log</span>
                        </v-tooltip>
                    </div>
                </div>
            </div>

            <!-- Content Section -->
            <v-card class="gl-breakdown-card" outlined data-testid="user-permission-add-access__content">
                <v-tabs v-model="tab" background-color="transparent" color="transparent" 
                class="gl-breakdown__tabs"  data-testid="gl-breakdown__tabs" height="40">
                    <v-tab key="revoke">{{isUserPermissionsEnabled ? 'Revoke Access' : 'Access Details'}}</v-tab>
                    <v-tab key="add" v-if="isUserPermissionsEnabled">Add Access</v-tab>
                </v-tabs>
                <v-divider/>
                <div class="d-flex flex-column gap-4">
                  <v-tabs-items v-model="tab" class="transparent">
                      <!-- Revoke Access Tab -->
                      <v-tab-item key="revoke">
                          <RevokeAccessTab
                              :items="formattedMyReportingUnits"
                              :loading="loading"
                              :totalRecords="sourceAssignedPagination.totalRecords"
                              :serverSide="sourceAssignedPagination.totalRecords > formattedMyReportingUnits.length"
                              :currentPage="sourceAssignedPagination.currentPage"
                              :is-permission-enabled="isUserPermissionsEnabled"
                              @revoke="handleRevoke"
                              @page-change="handleRevokeAccessPageChange"
                          />
                      </v-tab-item>

                      <!-- Add Access Tab -->
                      <v-tab-item key="add">
                          <AddAccessTab
                              :items="formattedAllReportingUnits"
                              :allItems="sourceAllReportingUnits"
                              :filterOptions="masterFilters"
                              :loading="loading"
                              :roles="rolesList"
                              :totalRecords="sourceAllPagination.totalRecords"
                              :serverSide="sourceAllPagination.totalRecords > formattedAllReportingUnits.length"
                              :currentPage="sourceAllPagination.currentPage"
                              :is-permission-enabled="isUserPermissionsEnabled"
                              @add="handleAdd"
                              @filter-data="handleFilterData"
                              @page-change="handleAddAccessPageChange"
                          />
                      </v-tab-item>
                  </v-tabs-items>
                </div>
            </v-card>
        </div>
    </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import { formatReportingUnitsData } from '../../../utils/myReportingUnitsUtils';
import RevokeAccessTab from './RevokeAccessTab.vue';
import AddAccessTab from './AddAccessTab.vue';

export default {
    components: {
        RevokeAccessTab,
        AddAccessTab
    },
    data() {
        return {
            tab: null, // Tab model
            loading: false,
            data: [],
            userId: null,
            cc: null,
            items: [
                {
                    text: 'User Permissions',
                    disabled: false,
                    href: 'breadcrumbs_dashboard',
                },
                {
                    text: 'Manage User Access',
                    disabled: false,
                    href: 'breadcrumbs_link_1',
                },
            ],
            rolesList: [],
            dropdownNotifications: [
                { name: 'email_alerts', labelName: 'Email Alerts' },
                { name: 'sms_alerts', labelName: 'SMS Alerts' },
                { name: 'push_notifications', labelName: 'Push Notifications' },
            ],
            activitylogconfiguserrole: {
                title: 'User Role Activity Log',
                search: true,
                headers: [
                    { text: 'Role', value: 'role' },
                    { text: 'Updated By', value: 'updated_by' },
                    { text: 'Date', value: 'created_at' }
                ]
            },
            addTabFilter: null,
        };
    },
    computed: {
        ...mapGetters(['isUserPermissionsEnabled']),
        ...mapGetters('varianceanalysis/reportingUnit', [
            'getRUFilters',
            'getMaterialityData'
        ]),
        isCurrentUser() {
             const user = this.$store.state.user;
             const currentUserId = user && (user._id || user.userid || user.id);
             // Ensure strict comparison of strings
             return this.userId && currentUserId && String(this.userId) === String(currentUserId);
        },
        ...mapGetters('varianceanalysis/userpermission', [
            'getAssignedReportingUnits',
            'getUnassignedReportingUnits',
            'getUserPermissionsList',
            'getUserPermissionsRoles'
        ]),
        masterFilters() {
            const filters = this.getRUFilters || {};
            const pickList = (...keys) => {
                for (let i = 0; i < keys.length; i++) {
                    const key = keys[i];
                    if (Array.isArray(filters?.[key]) && filters[key].length > 0) return filters[key];
                }
                return [];
            };

            // CommonFilter needs a `name` property for display, but for API calls we also need stable ids.
            // Preserve `_id` wherever possible.
            const formatOptions = (list) => {
                if (!Array.isArray(list)) return [];
                return list
                    .map((item) => {
                        if (item == null) return null;
                        if (typeof item === 'string' || typeof item === 'number') {
                            const name = String(item).trim();
                            return name ? { _id: name, name } : null;
                        }
                        if (typeof item === 'object') {
                            const id = item._id || item.id || item.value || item.code || item.key;
                            const name = String(item.name || item.label || item.value || item.code || '').trim();
                            if (!name && !id) return null;
                            return { ...item, _id: id || name, name: name || String(id) };
                        }
                        return null;
                    })
                    .filter(Boolean);
            };

            return {
                // RU Filters API commonly returns *_detail arrays
                network: formatOptions(pickList('network_detail', 'network', 'networklist')),
                region: formatOptions(pickList('region_detail', 'region', 'regionlist')),
                country: formatOptions(pickList('country_detail', 'country', 'countrylist')),
                // RU type can come under different keys across endpoints
                type: formatOptions(pickList('reportingunitTypes', 'ruTypes', 'rutype', 'type')),
            };
        },
        sourceAssignedPagination() {
            const storeData = this.getAssignedReportingUnits || {};
                const pagination = storeData?.pagination || storeData?.data?.pagination;
                if (pagination) return pagination;
                const rows = Array.isArray(storeData)
                     ? storeData
                     : (Array.isArray(storeData?.data) ? storeData.data : (Array.isArray(storeData?.data?.data) ? storeData.data.data : []));
                if (rows.length) return { totalRecords: rows.length, currentPage: 1, pageSize: rows.length };
                return { totalRecords: 0, currentPage: 1, pageSize: 100 };
        },
        formattedMyReportingUnits() {
             // For Revoke Tab: Using 'Assigned' RUs
             const storeData = this.getAssignedReportingUnits || {};
             let rawRows = [];
             let page = 0;
             let limit = 10000;

             if (Array.isArray(storeData)) {
                rawRows = storeData;
                 } else if (Array.isArray(storeData.data)) {
                     rawRows = storeData.data;
                 } else if (Array.isArray(storeData?.data?.data)) {
                     // Some APIs wrap rows under data.data and pagination under data.pagination
                     rawRows = storeData.data.data;
                 }

                 const pagination = storeData?.pagination || storeData?.data?.pagination;
                 if (pagination) {
                     page = (pagination.currentPage || 1) - 1;
                     limit = pagination.pageSize || 10000;
             }

             const { formattedArray } = formatReportingUnitsData(rawRows, page, limit);
             return formattedArray;
        },
        sourceAllReportingUnits() {
             // For Add Tab: Using 'Unassigned' RUs
             const storeData = this.getUnassignedReportingUnits || {};
             let rawRows = [];
             let page = 0;
             let limit = 10000;

             if (Array.isArray(storeData)) {
                rawRows = storeData;
                 } else if (Array.isArray(storeData.data)) {
                     rawRows = storeData.data;
                 } else if (Array.isArray(storeData?.data?.data)) {
                     // `fetchUnassignedReportingUnits` stores the full response object in Vuex.
                     // Rows live under `data.data` and pagination under `data.pagination`.
                     rawRows = storeData.data.data;
             }

                 const pagination = storeData?.pagination || storeData?.data?.pagination;
                 if (pagination) {
                     page = (pagination.currentPage || 1) - 1;
                     limit = pagination.pageSize || 10000;
                 }
             
             const { formattedArray } = formatReportingUnitsData(rawRows, page, limit);
             return formattedArray;
        },
        sourceAllPagination() {
            const storeData = this.getUnassignedReportingUnits || {};
                const pagination = storeData?.pagination || storeData?.data?.pagination;
                if (pagination) return pagination;
                const rows = Array.isArray(storeData)
                     ? storeData
                     : (Array.isArray(storeData?.data) ? storeData.data : (Array.isArray(storeData?.data?.data) ? storeData.data.data : []));
                if (rows.length) return { totalRecords: rows.length, currentPage: 1, pageSize: rows.length };
                return { totalRecords: 0, currentPage: 1, pageSize: 25 };
        },
        formattedAllReportingUnits() {
             const formattedArray = this.sourceAllReportingUnits;
             // If server side pagination is active (we have totalRecords > length), 
             // we rely on server filtering.
             // But for safety/legacy, we can keep client filtering if the list covers everything.
             // Here we just return the array because server filtering is preferred if we implement it.
             
             // However, for now, preserving logic: 
             // IF we assume we have partial data, client filtering is bad.
             // So I will opt to skip client filtering if I'm triggering server fetches.
             
             if (this.sourceAllPagination.totalRecords >= formattedArray.length) {
                 return formattedArray;
             }

             if (!this.addTabFilter) return formattedArray;
             if (Object.keys(this.addTabFilter).length === 0) return formattedArray;
             
             // Client-side filtering logic (fallback)
             const f = this.addTabFilter;
             return formattedArray.filter(row => {
                 let match = true;
                 
                 // Network
                 if (f.network && f.network.length > 0) {
                     const selectedNames = f.network.map(i => i.name);
                     if (!selectedNames.includes(row.network)) match = false;
                 }
                 
                 // Region
                 if (match && f.region && f.region.length > 0) {
                     const selectedNames = f.region.map(i => i.name);
                     if (!selectedNames.includes(row.region)) match = false;
                 }
                 
                 // Country
                 if (match && f.country && f.country.length > 0) {
                     const selectedNames = f.country.map(i => i.name);
                     if (!selectedNames.includes(row.country)) match = false;
                 }
                 
                 // RU Type
                 if (match && f.type && f.type.length > 0) {
                     const selectedNames = f.type.map(i => i.name);
                     if (!selectedNames.includes(row.type)) match = false;
                 }
                 
                 if (match && f.reportingunit && f.reportingunit.length > 0) {
                     const selectedIds = f.reportingunit.map(i => i._id);
                     if (!selectedIds.includes(row._id)) match = false;
                 }

                 return match;
             });
        },
        userName() {
            if (!this.data || !this.data.length || !this.userId) return '';
            const user = this.data.find(u => String(u.userid) === String(this.userId));
            return user?.user_detail?.name || user?.user_detail?.contactemail || '';
        },
        computedUserId() {
            if (this.data && this.data.length > 0) {
                const user = this.data.find(u => String(u.userid) === String(this.userId));
                if (user && user._id) return user._id;
            }
            return this.userId;
        },
    },
    created() {
        // Set userId from route params after component is created
        let rawId = this.$route.params.userId;
        // Sanitize userId just in case
        this.userId = rawId ? String(rawId).trim().replace(/['"]/g, '') : null;

        this.loading = true;
        Promise.all([
            this.getRole(),
            this.initData()
        ]).finally(() => {
            this.loading = false;
        });
    },
    methods: {
        ...mapActions('varianceanalysis/reportingUnit', [
            'fetchRUFilters',
            'fetchMateriality' // fetchAllReportingUnits removed as it's not used now
        ]),
        ...mapActions('varianceanalysis/userpermission', [
            'fetchAssignedReportingUnits',
            'fetchUnassignedReportingUnits',
            'fetchUserPermissions'
        ]),
        async initData(force = false) {
            try {
                 const client = this.$store.state.client;
                 if (!this.userId) {
                     console.warn('[UserPermissionAddAccess] User ID is missing');
                     return;
                 }

                 // 1. RU Filters
                 await this.fetchRUFilters({ filter: {}, axiosInstance: this.axios });
                 
                 // Get defaults from filters
                 const filters = this.getRUFilters || {};
                 // Prefer 'Actual' or first available, assume year/period arrays exist
                 const year = (filters.year && filters.year.length) ? filters.year[0] : new Date().getFullYear();
                 const period = (filters.period && filters.period.length) ? filters.period[0] : 'Jan';
                 
                 // Materiality uses fetchMateriality, usually for analysis, maybe not needed here? 
                 // Keeping it as it was in original code
                 const filterPayload = { 
                    user_id: [this.userId], 
                    limit: 100,
                    year: year,
                    period: period
                 };
                
                 const baseFilter = {
                    limit: 100
                 };
                //  const revokeFilter = {
                //     ru: "all",
                //     type: "all",
                //     region: "all",
                //     country: "all",
                //     network: "all",
                //     limit: 1000
                //  };

                 // 2. Assigned Reporting Units (Revoke Tab) - for selected user
                 await this.fetchAssignedReportingUnits({ 
                    userId: this.userId,
                    filter: baseFilter,
                    client: client,
                    axiosInstance: this.axios,
                    force
                 });
                 
                 // 3. Unassigned Reporting Units (Add Tab)
                 await this.fetchUnassignedReportingUnits({ 
                    userId: this.userId,
                    filter: baseFilter,
                    client: client,
                    axiosInstance: this.axios,
                    force
                 });
                 
                 // 4. Materiality
                 await this.fetchMateriality({
                    filter: filterPayload,
                    client: client,
                    axiosInstance: this.axios
                 });
                 
            } catch (error) {
                console.error("Error initializing data:", error);
                this.$store.commit("sbError", error.message || "Failed to load data");
            }
        },
        async getRole() {
            try {
                // Use store action to fetch permissions (uses cache if available)
                await this.fetchUserPermissions({ axiosInstance: this.axios, force: false });

                this.data = [];
                // Wait for getters to update
                this.$nextTick(() => {
                    // Deep copy from store to allow local modification
                    this.data = JSON.parse(JSON.stringify(this.getUserPermissionsList || []));
                    this.rolesList = JSON.parse(JSON.stringify(this.getUserPermissionsRoles || []));
                    
                    // Set default selected role
                    const user = this.data.find(u => String(u.userid) === String(this.userId));
                    if (user && user.role && user.role.length > 0) {
                        this.cc = user.role[0];
                    }
                });
            } catch (err) {
                this.$store.commit("sbError", err.message || err || "Unknown error!");
                console.log(err);
            }
        },
        handleChange(value) {
            this.loading = true;
            const user = this.data.find(u => String(u.userid) === String(this.userId));
            const id = user ? user._id : this.userId;
            this.axios.post("/v2/varianceanalysis/userpermission/edit/"+id, {data: {role: [value], userId: this.userId}}).then(dt => {
                if(dt.data.status === "success") {
                    this.$store.commit("sbSuccess","User permissions updated successfully");
                    // Update local data
                    const userIndex = this.data.findIndex(u => String(u.userid) === String(this.userId));
                    if (userIndex !== -1) {
                         // Create a new object for reactivity
                         const updatedUser = { ...this.data[userIndex] };
                         updatedUser.role = [value];
                         this.$set(this.data, userIndex, updatedUser);
                    }
                } else throw new Error ("Error updating role");
            }).catch(err => {
                this.$store.commit("sbError", err.response?.data?.message || err.message || "Unknown error!");
                console.log(err);
                // Revert selection if failed (optional but good UX)
                const user = this.data.find(u => String(u.userid) === String(this.userId));
                if (user && user.role && user.role.length > 0) {
                    this.cc = user.role[0];
                }
            }).finally(() => {
                this.loading = false;
            })
        },
        async handleRevoke(items) {
           const client = this.$store.state.client;
           
           if (!items || !items.length) return;
           if (!this.userId) {
               console.error('[UserPermissionAddAccess] Missing userId');
               this.$store.commit('sbError', 'Internal Error: User ID missing', { root: true });
               return;
           }

           const reportingUnitIds = items.map(item => item._id).filter(id => !!id);
           
           if (!reportingUnitIds.length) {
               console.error('[UserPermissionAddAccess] No valid IDs found in items', items);
               this.$store.commit('sbError', 'Internal Error: Invalid selection data', { root: true });
               return;
           }

           this.loading = true;

           try {
              await this.$store.dispatch('varianceanalysis/userpermission/removeUserAccess', {
                  userId: this.userId,
                  reportingUnitIds,
                  client,
                  axiosInstance: this.axios
              });
              
              this.$store.commit('sbSuccess', 'Access revoked successfully', { root: true });
              await this.initData(true);
           } catch (error) {
              const msg = error.response?.data?.message || error.message || 'Failed to revoke access';
              this.$store.commit('sbError', msg, { root: true });
              console.error(error);
           } finally {
              this.loading = false;
           }
        },
        async handleAdd(items) {
           const client = this.$store.state.client;
           
           if (!items || !items.length) return;
           if (!this.userId) {
               console.error('[UserPermissionAddAccess] Missing userId');
               this.$store.commit('sbError', 'Internal Error: User ID missing', { root: true });
               return;
           }

           const reportingUnitIds = items.map(item => item._id).filter(id => !!id);
           
           if (!reportingUnitIds.length) {
               console.error('[UserPermissionAddAccess] No valid IDs found in items', items);
               this.$store.commit('sbError', 'Internal Error: Invalid selection data', { root: true });
               return;
           }

           this.loading = true;

           try {
              await this.$store.dispatch('varianceanalysis/userpermission/addUserAccess', {
                  userId: this.userId,
                  reportingUnitIds,
                  client,
                  axiosInstance: this.axios
              });
              
              this.$store.commit('sbSuccess', 'Access added successfully', { root: true });
              await this.initData(true);
           } catch (error) {
              const msg = error.response?.data?.message || error.message || 'Failed to add access';
              this.$store.commit('sbError', msg, { root: true });
              console.error(error);
           } finally {
              this.loading = false;
           }
        },
        async handleRevokeAccessPageChange(page) {
             this.loading = true;
             try {
                 const client = this.$store.state.client;
                 const payloadFilter = {
                     limit: 100, 
                     page: page
                 };
                 
                 await this.fetchAssignedReportingUnits({ 
                    userId: this.userId,
                    filter: payloadFilter,
                    client: client,
                    axiosInstance: this.axios,
                    force: true
                 });
             } catch(e) {
                 console.error(e);
             } finally {
                 this.loading = false;
             }
        },
        handleRevokeFilterData() {
            // Future implementation for revoke tab filters
        },
        async handleAddAccessPageChange(page) {
             this.loading = true;
             try {
                 const client = this.$store.state.client;
                 const filter = this.addTabFilter || {};
                 
                 const payloadFilter = {
                     limit: 100, 
                     page: page,
                     ...filter
                 };
                 
                 await this.fetchUnassignedReportingUnits({ 
                    userId: this.userId,
                    filter: payloadFilter,
                    client: client,
                    axiosInstance: this.axios,
                    force: true
                 });
             } catch(e) {
                 console.error(e);
             } finally {
                 this.loading = false;
             }
        },
        async handleFilterData(filterEvents) {
            // Check if it's a reset event or regular filter
            // The CommonFilter emits { filter: {...} } usually
            const filterData = (filterEvents && filterEvents.filter) ? filterEvents.filter : (filterEvents || {});
            
            // Format for API: The backend usually expects arrays for mulit-selects
            // Transform { network: [{name: 'A'}, {name: 'B'}] } to { network: ['A', 'B'] } if needed
            // But checking your 'formatReportingUnitsData', it seems we filter locally too? 
            // Wait, we switched to server side filtering for pagination.
            // So we must pass filters to API.
            
            const apiFilter = {};
            // Unassigned reporting-units API expects hierarchy ids, not display names.
            if (filterData.network && filterData.network.length) apiFilter.network = filterData.network.map(x => x?._id || x?.id || x?.name || x);
            if (filterData.region && filterData.region.length) apiFilter.region = filterData.region.map(x => x?._id || x?.id || x?.name || x);
            if (filterData.country && filterData.country.length) apiFilter.country = filterData.country.map(x => x?._id || x?.id || x?.name || x);
            if (filterData.type && filterData.type.length) {
                const ruTypes = filterData.type.map(x => x?._id || x?.id || x?.name || x);
                apiFilter.type = ruTypes;
                apiFilter.ruTypes = ruTypes;
                apiFilter.rutype = ruTypes;
            }
            if (filterData.reportingunit && filterData.reportingunit.length) apiFilter.reportingunit = filterData.reportingunit.map(x => x?._id || x);

            this.addTabFilter = apiFilter;
            this.handleAddAccessPageChange(1); // Reset to page 1 on filter
        }
    },
    watch: {
       'sourceAllReportingUnits': {
            handler() {
                 // Trigger re-calc of computed if needed, though Vue handles this.
            }
       }
    },
    beforeDestroy() {
        // Reset component state to prevent stale data
        this.userId = null;
        this.cc = null;
    },
};
</script>

<style scoped>
/* Shimmer loading animation */
.breadcrumb-shimmer {
  display: inline-block;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.breadcrumb-shimmer--title {
  width: 200px;
  height: 22px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.gl-breakdown__tabs ::v-deep .v-tab{
  font-size: var(--font-size-sm, 16px);
  font-weight: 400;
  color: var(--color-tabs-header);
  padding: var(--space-2) var(--space-8);
  letter-spacing: normal;
}
.gl-breakdown__tabs ::v-deep .v-tab--active{
  font-weight: 600;
  color: var(--color-tabs-header-active);
}
.gl-breakdown-card {
  padding: var(--space-9) var(--space-6);
}
</style>